/* Developer : Nithin
Date : 26-04-2026
changes: conditionally display and default Yes/No for Related to Project field */
import React, { useState, useEffect } from "react";
import { Modal, FormInput, FormSelect, FormTextarea } from "@/components/ui";
import { ROLE_LABELS } from "@/constants/globalConstants";
import {
  Send,
  Save,
  Search
} from "lucide-react";
import toast from "react-hot-toast";

export const AddGeneralExpenseModal = ({ isOpen, onClose, onConfirm, editData = null, mode = "add", employees = [], projects = [], hideProjectSelection = false, customTitle = null, user = null }) => {
  const [form, setForm] = useState({
    amount: "",
    paymentMethod: "cash",
    date: new Date().toISOString().split('T')[0],
    reason: "",
    toWhomPaid: "",
    byWhomPaid: "",
    requestedBy: "",
    notes: "",
    relatedToProject: "no",
    relatedProjectId: "",
    relatedProjectCollection: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  const editDataStr = JSON.stringify(editData);

  // Populate form when editData changes
  useEffect(() => {
    if (editData && mode === "edit") {
      setForm({
        amount: Math.abs(editData.amount),
        paymentMethod: editData.paymentMethod || "cash",
        date: new Date(editData.date).toISOString().split('T')[0],
        reason: editData.reason || "",
        toWhomPaid: editData.toWhomPaid || "",
        byWhomPaid: editData.byWhomPaid || (editData.requestedBy?.name || ""),
        requestedBy: typeof editData.requestedBy === 'object' ? editData.requestedBy?._id : (editData.requestedBy || ""),
        notes: editData.notes || "",
        relatedToProject: editData.relatedToProject ? "yes" : "no",
        relatedProjectId: editData.relatedProjectId?._id || editData.relatedProjectId || "",
        relatedProjectCollection: editData.relatedProjectCollection || "",
        category: editData.category || (editData.relatedToProject ? "project_related" : "general"),
      });

      const pId = editData.relatedProjectId?._id || editData.relatedProjectId;
      if (pId) {
        const p = projects.find(proj => proj._id === pId || proj.projectId === pId) || (typeof editData.relatedProjectId === 'object' ? editData.relatedProjectId : null);
        if (p) {
          setProjectSearch(`${p.customerCustomId || p.projectId || ''} — ${p.customerName || ''}`);
        }
      }
    } else {
      // Auto-select current user in Add mode
      //changed related to sales head form submission

      let defaultRequestedBy = "";
      let defaultByWhomPaid = "";
      //changed related to sales head form submission

      // Try to find in employees list first for consistency
      const currentUser = employees.find(e => e._id === user?._id || e.userId === user?._id);

      if (currentUser) {
        defaultRequestedBy = currentUser._id;
        defaultByWhomPaid = currentUser.name || "";
        //changed related to sales head form submission

      } else if (user) {
        // Fallback to user object directly if not in employees list (Fixes validation error for Sales Head)
        defaultRequestedBy = user._id;
        defaultByWhomPaid = (user.name || `${user.firstName || ""} ${user.lastName || ""}`).trim();
      }

      const isProjectMode = customTitle?.includes("Project Expense");
      const initialRelatedToProject = editData ? (editData.relatedToProject === "yes" || editData.relatedToProject === true ? "yes" : "no") : (isProjectMode ? "yes" : "no");
      const initialRelatedProjectId = editData?.relatedProjectId?._id || editData?.relatedProjectId || (isProjectMode && projects.length === 1 ? (projects[0]._id || projects[0].projectId) : "");
      const initialRelatedProjectCollection = editData?.relatedProjectCollection || (isProjectMode && projects.length === 1 ? (projects[0].model || 'VerveProjectExecution') : "");

      setForm({
        amount: "",
        paymentMethod: "cash",
        date: new Date().toISOString().split('T')[0],
        reason: "",
        toWhomPaid: "",
        byWhomPaid: defaultByWhomPaid,
        requestedBy: defaultRequestedBy,
        notes: "",
        relatedToProject: initialRelatedToProject,
        relatedProjectId: initialRelatedProjectId,
        relatedProjectCollection: initialRelatedProjectCollection,
        category: editData?.category || ((editData?.relatedToProject || projects.length === 1) ? "project_related" : "general"),
      });

      if (initialRelatedProjectId && projects.length === 1) {
        const p = projects[0];
        setProjectSearch(`${p.customerCustomId || p.projectId} — ${p.customerName}`);
      } else {
        setProjectSearch("");
      }
    }
  }, [editDataStr, mode, isOpen, user, employees, projects.length]);

  const filteredProjects = React.useMemo(() => {
    if (!projectSearch) return projects;
    return projects.filter(p =>
      (p.customerCustomId || p.projectId || '').toLowerCase().includes(projectSearch.toLowerCase()) ||
      (p.customerName || '').toLowerCase().includes(projectSearch.toLowerCase())
    );
  }, [projects, projectSearch]);

  const handleProjectSelect = (p) => {
    setForm(prev => ({
      ...prev,
      relatedProjectId: p._id || p.projectId,
      relatedProjectCollection: p.model || 'VerveProjectExecution'
    }));
    setProjectSearch(`${p.customerCustomId || p.projectId} — ${p.customerName}`);
    setShowProjectDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.reason || !form.toWhomPaid || !form.byWhomPaid || !form.date) {
      toast.error("Please fill all required fields");
      return;
    }
    if (form.relatedToProject === "yes" && !form.relatedProjectId) {
      toast.error("Please select a project ID");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        relatedToProject: (form.relatedToProject === "yes" || form.category === "advance"),
        relatedProjectId: (form.relatedToProject === "yes" || form.category === "advance") ? (form.relatedProjectId || projects[0]?._id || projects[0]?.projectId || null) : null,
        relatedProjectCollection: (form.relatedToProject === "yes" || form.category === "advance") ? (form.relatedProjectCollection || projects[0]?.model || 'VerveProjectExecution') : null,
      };
      const success = await onConfirm(payload);
      if (success) {
        toast.success(mode === "edit" ? "Expense updated successfully" : "General expense recorded successfully");
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save expense");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={customTitle || (mode === "edit" ? "Edit General Expense" : "Request General Expense")} size="max-w-2xl">
      {customTitle === "Request Project Expense" && (
        <div className="mb-4 text-[11px] font-medium text-slate-500">
          This form is used for expenses related to project, advance paid to customer.
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Row 1: Request by, request date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput
            label="Request by"
            value={mode === "add" ? (((user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`).trim() || "User") + ` (${ROLE_LABELS[user?.role] || user?.role || "Staff Member"})`) : (form.byWhomPaid || "User")}
            readOnly
            required
          />
          <FormInput
            label="Request date"
            type="date"
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            required
          />
        </div>

        {/* Row 2: Amount, To whom */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput
            label="Amount"
            type="number"
            placeholder="0.00"
            value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })}
            required
          />
          <FormInput
            label="To whom"
            type="text"
            placeholder="e.g. Reliance Power"
            value={form.toWhomPaid}
            onChange={e => setForm({ ...form, toWhomPaid: e.target.value })}
            required
          />
        </div>

        {/* Row 3: Request for reason */}
        <FormInput
          label="Request for reason"
          type="text"
          placeholder="e.g. Office Electricity Bill"
          value={form.reason}
          onChange={e => setForm({ ...form, reason: e.target.value })}
          required
        />

        {/* Row between reason and notes: Related to Project */}
        <div>
          <label className="flex items-center gap-1.5 text-[13px] font-medium text-slate-500 mb-1">Related to Project?</label>
          <div className="flex items-center gap-6 mt-1">
            <div className="flex items-center gap-4">
              {(customTitle?.includes("Project Expense") ? ["yes"] : ["no"]).map(opt => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="relatedToProject"
                    value={opt}
                    checked={form.relatedToProject === opt}
                    onChange={e => {
                      const val = e.target.value;
                      let pid = form.relatedProjectId;
                      let coll = form.relatedProjectCollection;
                      let cat = form.category;
                      if (val === "yes" && !pid && projects.length === 1) {
                        pid = projects[0]._id || projects[0].projectId;
                        coll = projects[0].model || 'VerveProjectExecution';
                        setProjectSearch(`${projects[0].customerCustomId || projects[0].projectId} — ${projects[0].customerName}`);
                        cat = "project_related";
                      } else if (val === "no") {
                        pid = "";
                        coll = "";
                        setProjectSearch("");
                        cat = "general";
                      } else if (val === "yes") {
                        cat = "project_related";
                      }
                      setForm({ ...form, relatedToProject: val, relatedProjectId: pid, relatedProjectCollection: coll, category: cat });
                    }}
                    className="accent-brand-blue w-4 h-4"
                  />
                  <span className="text-[13px] font-medium text-slate-600 capitalize">{opt === "yes" ? "Yes" : "No"}</span>
                </label>
              ))}
            </div>
            {form.relatedToProject === "yes" && (
              <div className="flex-1 flex flex-row items-start gap-3">
                <div className={`flex-1 ${hideProjectSelection ? 'hidden' : ''}`}>
                <div className="relative group">
                  <FormInput
                    placeholder="Search Project / ID..."
                    value={projectSearch}
                    onChange={(e) => {
                      setProjectSearch(e.target.value);
                      setShowProjectDropdown(true);
                    }}
                    onFocus={() => setShowProjectDropdown(true)}
                    required={form.relatedToProject === "yes" && !hideProjectSelection && !form.relatedProjectId}
                    rightIcon={<Search size={14} />}
                  />

                  {showProjectDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-56 overflow-y-auto no-scrollbar py-2">
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((p, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="w-full px-4 py-2.5 text-left hover:bg-blue-50/50 transition-colors flex flex-col gap-0.5 border-b border-slate-50 last:border-0"
                            onClick={() => handleProjectSelect(p)}
                          >
                            <span className="text-xs font-bold text-brand-dark">{p.customerName}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] font-black text-brand-blue bg-blue-50 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                {p.customerCustomId || p.projectId}
                              </span>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                          No matching projects
                        </div>
                      )}
                    </div>
                  )}
                  {showProjectDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowProjectDropdown(false)} />}
                </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Row 4: Notes */}
        <FormTextarea
          label="Notes"
          placeholder="Describe the expense detail..."
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          rows={3}
        />

        <div className="flex gap-3 mt-5 pt-4 border-t border-slate-100">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {submitting ? (
              <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                {mode === "edit" ? <Save size={14} /> : <Send size={14} />}
                {mode === "edit" ? "Update Expense" : "Request for Approval"}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};